import './polyfills';
import extend from 'node.extend';
import parser from 'ua-parser-js';
import {nanoid} from 'nanoid';
import Cookies from 'js-cookie';
import util, {noop, getHrefQueryParamValue, getBaseConfig} from './util';

const APP_MAP = {
    MMQ: 'mmq',
    PT: 'pt',
    WXAPP: 'wxapp',
    WXMINI: 'wxmini'
};

function getPromiseTuple() {
    let resolve = null;
    let reject = null;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });

    return [promise, resolve, reject];
}

function ensureCookiesId() {
    return new Promise(resolve => {
        const key = 'MREPORT_COOKIEID';
        let cookieid = window.sessionStorage.getItem(key);
        if (!cookieid) {
            cookieid = nanoid();
            window.sessionStorage.setItem(key, cookieid);
        }
        resolve(cookieid);
    });
}

function getUaParser() {
    const parserResult = parser(window.navigator.userAgent);
    const result = {
        open_ov: parserResult.os.version || '',
        app: parserResult.browser.name || '',
        app_ver: parserResult.browser.version || ''
    };
    if (window.MJSSDK) {
        const ua = window.MJSSDK.UA;
        result.app_ver = ua.appVer;
        if (ua.mmq) {
            result.app = 'mmq';
        } else if (ua.pregnancyhelper) {
            result.app = 'qt';
        } else if (ua.wx) {
            result.app = 'wx';
        } else {
            result.app = 'wap';
        }
    }
    return result;
}

function getOpenOsValue() {
    const openos = {
        ios: 1,
        android: 2,
        wap: 3,
        pc: 4,
        other: 5
    };
    const ua = window.navigator.userAgent;
    const isAndroid = /Android|Adr/i.test(ua);
    const isIOS = /\(i[^;]+;( U;)? CPU.+Mac OS X/.test(ua);
    const isWap = /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i.test(
        ua
    );
    if (util.isMamaApp) {
        if (isIOS) {
            return openos.ios;
        } else if (isAndroid) {
            return openos.android;
        } else {
            return openos.other;
        }
    } else if (isWap) {
        return openos.wap;
    } else {
        return openos.pc;
    }
}

function getBaseData() {
    const uaParserResult = getUaParser();
    return {
        user: {
            uid: Cookies.get('user_id') || '',
            open_o: getOpenOsValue(),
            open_ov: uaParserResult.open_ov || '',
            open_mmid: '',
            cookieid: ''
        },
        event: '',
        properties: {
            itemid: '',
            item_type: '',
            position: '',
            time: '',
            sessionid: '',
            item_mark: {
                item_name: '',
                item_order: '',
                item_url: window.location.href,
                item_desc: '',
                entrance: '',
                inside: '',
                outside: '',
                fm: '',
                channel: '',
                time_on_visit: '',
                item_mark_1: '',
                item_mark_2: '',
                item_mark_3: '',
                item_mark_4: '',
                item_mark_5: '',
                item_mark_6: '',
                item_mark_7: '',
                item_mark_8: ''
            }
        },
        content: {
            app: uaParserResult.app,
            app_ver: uaParserResult.app_ver,
            action: 'view_detail',
            contextid: '-1',
            context_type: '',
            context_mark: {
                context_name: ''
            },
            search_keyword: '',
            referrer: document.referrer
        }
    };
}

class DataStructure {
    /**
     *
     * @param {*} baseConfig
     */
    constructor(baseConfig = getBaseConfig(), getDeviceIdPermission) {
        // 由于基础数据需要异步获取 MJSSDK 数据，因此外部上报需要在等待数据 Ready 后再上报。
        this.baseData = getBaseData();
        this.getDeviceIdPermission = getDeviceIdPermission;
        this.dataReadyPromise = Promise.all([
            this.resolveUserCookieidParam(),
            this.resolveUserDeviceParam(),
            this.resolveContentAppParam(),
            this.resolveContext(baseConfig)
        ])
            .then(noop)
            .catch(noop);
    }

    resolveUserCookieidParam() {
        const [cookieidP, cookieidResolved] = getPromiseTuple();
        ensureCookiesId().then(cookieid => {
            this.extend({
                user: {
                    cookieid
                }
            });
            cookieidResolved();
        });
        return cookieidP;
    }

    resolveUserDeviceParam() {
        if (util.isMamaApp && window.MJSSDK) {
            return this.resolveDeviceInfo();
        }
        return Promise.resolve();
    }

    resolveDeviceInfo() {
        const [deviceP, deviceResolved] = getPromiseTuple();
        if (typeof this.getDeviceIdPermission === 'function') {
            this.getDeviceIdPermission()
                .then(() => {
                    window.MJSSDK.getDeviceID({
                        success: res => {
                            this.extend({
                                user: {
                                    open_mmid: res.deviceID
                                }
                            });
                            deviceResolved();
                        }
                    });
                })
                .catch(() => deviceResolved());
            return deviceP;
        }

        if (window.MJSSDK && typeof window.MJSSDK.getUnifiedDeviceID === 'function') {
            // 如果 MJSSDK 支持归一化设备号，我们优先使用。
            window.MJSSDK.getUnifiedDeviceID({
                success: res => {
                    this.extend({
                        user: {
                            open_mmid: res.deviceId
                        }
                    });
                    deviceResolved();
                }
            });
            return deviceP;
        }

        deviceResolved();
        return deviceP;
    }

    resolveContentAppParam() {
        const content = {
            app: ''
        };
        if (window['__wxjs_environment'] === 'miniprogram') {
            content.app = APP_MAP.WXMINI;
        } else if (window.MJSSDK && window.MJSSDK.UA.wx) {
            content.app = APP_MAP.WXAPP;
        } else if (window.MJSSDK && window.MJSSDK.UA.pregnancyhelper) {
            content.app = APP_MAP.PT;
        } else if (window.MJSSDK && window.MJSSDK.UA.mmq) {
            content.app = APP_MAP.MMQ;
        }

        this.extend({
            content
        });
    }

    resolveContext(baseConfig) {
        this.extend({
            content: {
                contextid: getHrefQueryParamValue(baseConfig.sourceQueryParam) || '-1',
                context_type: baseConfig.context_type
            }
        });
    }

    extend(inputData) {
        this.baseData = extend(true, this.baseData, inputData);
        return this;
    }

    reset() {
        this.baseData.properties = getBaseData().properties;
    }

    ready(fn = noop) {
        this.dataReadyPromise.then(fn);
    }
}

export default DataStructure;
